import { Button, CardBody, CardHeader, Col, FormGroup, Input, InputGroup, InputGroupText, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";

import { useTheme } from "../../../hooks/useTheme";
import { useCallback, useState, useMemo, useContext, useRef, useEffect } from "react";
import DataTableWindowed from "../../../components/DataTableWindowed";
import { NestQuoteItems, convertItemLookupTypesToColumnDefinitions, sanitizeForId } from "../functions/quoteFunctions";
import QuoteItemNameWithValidation from "./quoteItemNameWithValidation";
import QuoteItemGroupNameWithValidation from "./quoteItemGroupNameWithValidation";
import NestedTableQuoteItemColumn from "./NestedTableQuoteItemColumn";
import { QuoteDataContext } from "../contexts/quoteDataContext";  
import { RoundToTwoDecimalPlaces } from "../../../utils/numberUtils"
import { useLockState } from "../../../components/userLocks/LockCheck";
import QuoteItemBulkModal from "./quoteItemBulkModal";
import { useQuoteItemTableUpdater } from "../../../hooks/useQuoteItemTableUpdater";
import NestedTableSwitchColumn from "./NestedTableSwitchColumn";
import ActionButton from "../../../components/actionButton";
import { formatCurrency } from "../../../utils/numberUtils";

const CommercialTable = ({ quoteModules, quoteItems, updateQuoteItem, updateQuoteItems }) => {
    const theme = useTheme();
    const lockState = useLockState();
    const [editRow] = useQuoteItemTableUpdater(quoteItems, updateQuoteItem, updateQuoteItems);

    const { itemLookupTypes, suppliers, salesforceCodes} = useContext(QuoteDataContext);

    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Derive selected items from live quoteItems so bulk modal always gets up-to-date data
    const selectedItems = useMemo(
        () => quoteItems.filter(x => selectedIds.has(x.id)),
        [quoteItems, selectedIds]
    );

    const [isUpdating, setIsUpdating] = useState(false);

    const handleBulkSave = useCallback(async (updatedRows) => {
        setIsUpdating(true);
        try {
            await updateQuoteItems?.(updatedRows.map(row => ({ ...row })));
        } finally {
            setIsUpdating(false);
        }
    }, [updateQuoteItems]);    

    const [currentActiveMainTab, setCurrentActiveMainTab] = useState("All");
    const COLUMN_SETTINGS_KEYS_PREFIX = "quoteCommercialsTableColumnSettings_"
    const currentProfileKey = COLUMN_SETTINGS_KEYS_PREFIX + currentActiveMainTab;

    const filteredItems = useMemo(() => {
        return quoteItems.filter(x =>
            currentActiveMainTab === "Custom" ||
            currentActiveMainTab === "All" ||
            (x.isUpfront && currentActiveMainTab === "Upfront") ||
            x.isRecurring && ["Recurring", "All"].includes(currentActiveMainTab)
        )
    }, [quoteItems, currentActiveMainTab]);

    const data = useMemo(() => NestQuoteItems(filteredItems, quoteModules, salesforceCodes), [filteredItems, quoteModules, salesforceCodes]);

    const inUseItemLookupFields = useMemo(() => {
        if (quoteItems.length === 0 || !itemLookupTypes) {
            return []
        }
        const inUseItemLookupTypes = itemLookupTypes.filter(x => quoteItems.some(qi => qi.itemLookupTypeId === x.id))
        const inUseItemLookupFields = convertItemLookupTypesToColumnDefinitions(inUseItemLookupTypes);

        // Remove duplicates based on label
        const uniqueFields = Array.from(
            new Map(inUseItemLookupFields.map(field => [field.label, field])).values()
        );

        return uniqueFields;
    }, [quoteItems, itemLookupTypes])

    const itemLookupTypeMap = useMemo(
        () => new Map((itemLookupTypes ?? []).map(t => [t.id, t.itemLookupTypeName])),
        [itemLookupTypes]
    );
    const supplierMap = useMemo(
        () => new Map((suppliers ?? []).map(s => [s.id, s.supplierName])),
        [suppliers]
    );

    const loggedOutdatedWithoutLatestRef = useRef(new Set());
    useEffect(() => {
        const inconsistentItems = (quoteItems || []).filter(item => item?.commercialsAreOutdated && !item?.latestCommercials);
        inconsistentItems.forEach(item => {
            const key = `${item?.id ?? "unknown"}:${item?.guid ?? "unknown"}`;
            if (loggedOutdatedWithoutLatestRef.current.has(key)) return;
            loggedOutdatedWithoutLatestRef.current.add(key);
            console.warn("[CommercialTable] Row marked outdated without latestCommercials", {
                id: item?.id,
                guid: item?.guid,
                itemLookupId: item?.itemLookupId,
                itemLookupVersionId: item?.itemLookupVersionId,
                internalName: item?.internalName,
                source: item?.source,
            });
        });
    }, [quoteItems]);

    const buildCatalogueCommercialUpdate = useCallback((row) => {
        const latest = row?.latestCommercials;

        if (!latest) {
            return {
                ...row,
                id: row?.databaseId,
            };
        }

        return {
        ...row,
        id: row.databaseId,

        // Update the commercial terms to match the latest commercials.
        // This allows the user to easily update the quote item to reflect changes
        // in the commercial terms, without having to manually update each field.
        isUpfront: latest.isUpfront,
        isRecurring: latest.isRecurring,
        
        originalUpfrontCost: latest.isUpfront ? latest.upfrontCost : row.originalUpfrontCost,
        originalUpfrontPrice: latest.isUpfront ? latest.upfrontPrice : row.originalUpfrontPrice,
        originalRecurringCost: latest.isRecurring ? latest.recurringCost : row.originalRecurringCost,
        originalRecurringPrice: latest.isRecurring ? latest.recurringPrice : row.originalRecurringPrice,

        recurringNumPayments: !latest.isRecurring || row.recurringNumPayments ? row.recurringNumPayments : latest.recurringNumPayments,
        recurringIntervalMonths: !latest.isRecurring || row.recurringIntervalMonths ? row.recurringIntervalMonths : latest.recurringIntervalMonths,
        recurringStartMonth: !latest.isRecurring || row.recurringStartMonth ? row.recurringStartMonth : latest.recurringStartMonth,
        upfrontRecognitionMonths: !latest.isUpfront || row.upfrontRecognitionMonths ? row.upfrontRecognitionMonths : latest.upfrontRecognitionMonths,

        upfrontSalesforceCode: latest.isUpfront ? latest.upfrontSalesforceCode : row.upfrontSalesforceCode,
        recurringSalesforceCode: latest.isRecurring ? latest.recurringSalesforceCode : row.recurringSalesforceCode,

        itemLookupVersionId: latest.id,

        // If there is no list cost/price, or the user hasn't changed it from the list cost/price, then update to the latest commercials.
        // Otherwise, leave it as is, to avoid overwriting user changes.
        upfrontCost: latest.isUpfront && (row.originalUpfrontCost === null || row.upfrontCost === row.originalUpfrontCost) ? latest.upfrontCost : row.upfrontCost,
        upfrontPrice: latest.isUpfront && (row.originalUpfrontPrice === null || row.upfrontPrice === row.originalUpfrontPrice) ? latest.upfrontPrice : row.upfrontPrice,
        recurringCost: latest.isRecurring && (row.originalRecurringCost === null || row.recurringCost === row.originalRecurringCost) ? latest.recurringCost : row.recurringCost,
        recurringPrice: latest.isRecurring && (row.originalRecurringPrice === null || row.recurringPrice === row.originalRecurringPrice) ? latest.recurringPrice : row.recurringPrice,

        supplierId: latest.supplierId,
        autotaskCode: latest.autotaskCode
        };
    }, []);

    const getCatalogueCommercialChangeSummary = useCallback((row) => {
        const next = buildCatalogueCommercialUpdate(row);
            const normalize = (value, key) => {
                if (value === null || value === undefined || value === "") return "(empty)";
                if (key === "supplierId") return supplierMap.get(value) ?? "(unknown supplier)";
                if (typeof value === "boolean") return value ? "Yes" : "No";
            if (typeof value === "number") {
                if (key.includes("Price") || key.includes("Cost") || key.includes("Margin")) {
                    return Number.isFinite(value) ? "£" + formatCurrency(value) : "(empty)";    
                } else {
                    return Number.isFinite(value) ? value.toString() : "(empty)";
                }

            } 
            return String(value);
        };

        const fields = [
            ["isUpfront", "Upfront billing"],
            ["isRecurring", "Recurring billing"],
            ["originalUpfrontCost", "Unit upfront list cost"],
            ["originalUpfrontPrice", "Unit upfront list sell"],
            ["originalRecurringCost", "Unit recurring list cost"],
            ["originalRecurringPrice", "Unit recurring list sell"],
            ["upfrontCost", "Unit upfront cost"],
            ["upfrontPrice", "Unit upfront sell"],
            ["recurringCost", "Unit recurring cost"],
            ["recurringPrice", "Unit recurring sell"],
            /*
            // Temporarily remove these four fields from being checked, as they are almost always
            // blank in the catalogue, and set in the basket items.
            ["recurringStartMonth", "Start month"],
            ["recurringIntervalMonths", "Interval months"],
            ["recurringNumPayments", "Number of payments"],
            ["upfrontRecognitionMonths", "Upfront recognition months"],
            */
            ["upfrontSalesforceCode", "Upfront Salesforce code"],
            ["recurringSalesforceCode", "Recurring Salesforce code"],
            ["autotaskCode", "Autotask code"],
            ["supplierId", "Supplier"]
        ];

        return fields
            .filter(([key]) => normalize(row?.[key], key) !== normalize(next?.[key], key))
            .map(([key, label]) => ({
                key,
                label,
                from: normalize(row?.[key], key),
                to: normalize(next?.[key], key),
            }));
    }, [buildCatalogueCommercialUpdate, supplierMap]);

    const buildRestoreCommercialUpdate = useCallback((row) => ({
        ...row,
        id: row.databaseId,

        // Revert the commercial terms back to the original commercials.
        upfrontCost: row.originalUpfrontCost,
        upfrontPrice: row.originalUpfrontPrice,
        recurringCost: row.originalRecurringCost,
        recurringPrice: row.originalRecurringPrice,
    }), []);

    const getRestoreCommercialChangeSummary = useCallback((row) => {
        const next = buildRestoreCommercialUpdate(row);
        const normalize = (value, key) => {
            if (value === null || value === undefined || value === "") return "(empty)";
            if (typeof value === "number") {
                if (key.includes("Price") || key.includes("Cost") || key.includes("Margin")) {
                    return Number.isFinite(value) ? "£" + formatCurrency(value) : "(empty)";
                }
                return Number.isFinite(value) ? value.toString() : "(empty)";
            }
            return String(value);
        };

        const fields = [
            ["upfrontCost", "Unit upfront cost"],
            ["upfrontPrice", "Unit upfront sell"],
            ["recurringCost", "Unit recurring cost"],
            ["recurringPrice", "Unit recurring sell"],
        ];

        return fields
            .filter(([key]) => normalize(row?.[key], key) !== normalize(next?.[key], key))
            .map(([key, label]) => ({
                key,
                label,
                from: normalize(row?.[key], key),
                to: normalize(next?.[key], key),
            }));
    }, [buildRestoreCommercialUpdate]);

    const mainColumns = useMemo(() => [
        {
            key: "source",
            label: "Source Icon",
            header: "Source",   
            isFixed: true,  
            style: { width: "50px" },          
            render: (value, row) => {
                return <NestedTableQuoteItemColumn row={row} columnKey="source" />;
            }
        },
        {
            key: "itemNumber",
            label: "Item #",
            header: "#",
            dataType: "number",
            align: "left",
            showInFooter: false,
            style: { width: "20px" },
            isFixed: true,
            render: (value, row) => {
                return <div style={{width:"100%", textAlign: "right"}}>{value}</div>
            }
        },
        {
            key: "description",
            label: "Name",
            header: <span><i className={`bi bi-exclamation-triangle me-2`} style={{ visibility: "hidden" }}></i>Name</span>, /* Hidden icon is just there for spacing reasons, as the data rows can have an icon before the name */
            align: "left",
            style: { width: "250px" },
            showInFooter: true,
            isFixed: true,
            renderFooter: (value, footerData, allData) => {
                return <>TOTAL:</>
            },
            dataType: "text",
            render: (value, row, isExpanded) => {
                return (
                    <>
                        {!row.topLevelGroupRow &&
                            <QuoteItemNameWithValidation item={row}/>
                        }
                        {row.topLevelGroupRow &&
                            <QuoteItemGroupNameWithValidation group={row} />
                        }
                    </>
                )
            }
        },
        {
            key: "nameOnQuote",
            label: "Name on Quote",
            align: "left",
            style: { width: "300px" },
            showInFooter: false,
            isFixed: true,
            dataType: "text",
            searchValue: (value, row) => {
                if (row.isUpfront && row.isRecurring) {
                    return `${row.upfrontNameOnQuote ?? ""} ${row.recurringNameOnQuote ?? ""}`
                } else if (row.isUpfront) {
                    return row.upfrontNameOnQuote ?? ""
                } else if (row.isRecurring) {
                    return row.recurringNameOnQuote ?? ""
                }
                return ""
            },
            render: (value, row, isExpanded) => {
                const fieldName = "nameOnQuote";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, e.target.name, e.target.value)}
                    />
                )
            }
        },
        {
            key: "autotaskCode",
            label: "Autotask Code",
            align: "left",
            style: { width: "250px" },
            showInFooter: false,
            dataType: "text",
            render: (value, row, isExpanded) => {
                return (
                    value
                )
            }
        },
        {
            key: "salesforceCode",
            label: "Salesforce Code",
            align: "left",
            style: { width: "350px" },
            showInFooter: false,
            dataType: "text",
            searchValue: (value, row) => {
                if (row.isUpfront && row.isRecurring) {
                    return `${row.upfrontSalesforceCode ?? ""} ${row.recurringSalesforceCode ?? ""}`
                } else if (row.isUpfront) {
                    return row.upfrontSalesforceCode ?? ""
                } else if (row.isRecurring) {
                    return row.recurringSalesforceCode ?? ""
                }
                return ""
            },
            render: (value, row, isExpanded) => {
               const fieldName = "salesforceCode";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, e.target.name, e.target.value)}
                    />
                )
             }
        },
        {
            key: "itemLookupTypeId",
            label: "Type",           
            align: "left",
            style: { width: "180px" },
            showInFooter: false,
            dataType: "text",
            searchValue: (value) => itemLookupTypeMap.get(value) ?? "",
            render: (value) => {
                return itemLookupTypes?.find(x => x.id === value)?.itemLookupTypeName
            }
        },
        {
            key: "quantity",
            label: "Quantity",
            className: "p-1",
            style: { width: "80px" },
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"quantity"}
                        disabled={row.quoteModuleGuid || row.source === "Basket Processing" || lockState?.isLocked}
                        onChange={(e) => editRow(row, "quantity", e.target.value)}
                    />
                )
            }
        },
        {
            key: "supplierId",
            label: "Supplier",
            style: { width: "180px" },
            dataType: "text",
            searchValue: (value) => supplierMap.get(value) ?? "",
            render: (value) => {
                return suppliers?.find(x => x.id === value)?.supplierName
            }
        }      
    ].map(x => ({...x, group: "Properties"})), [editRow, itemLookupTypeMap, supplierMap, lockState?.isLocked]);

    const upfrontColumns = [
        {
            key: "originalUpfrontCost",
            label: "Unit Upfront List Cost",
            header: <>Unit List Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"originalUpfrontCost"}
                        disabled={true}
                        onChange={(e) => editRow(row, "originalUpfrontCost", e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontCost",
            label: "Unit Upfront Cost",
            header: <>Unit Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"upfrontCost"}
                        disabled={!row.isUpfront}
                        onChange={(e) => editRow(row, "upfrontCost", e.target.value)}
                    />
                )
            }
        },
        {
            key: "originalUpfrontPrice",
            label: "Unit Upfront List Sell",
            header: <>Unit List Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"originalUpfrontPrice"}
                        disabled={true}
                        onChange={(e) => editRow(row, "originalUpfrontPrice", e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontPrice",
            label: "Unit Upfront Sell",
            header: <>Unit Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"upfrontPrice"}
                        disabled={!row.isUpfront}
                        onChange={(e) => editRow(row, "upfrontPrice", e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontMarginValue",
            label: "Unit Upfront Margin £",
            header: <>£<br />Unit Margin</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"upfrontMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "upfrontMarginValue", e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontMargin",
            label: "Unit Upfront Margin %",
            header: <>%<br />Unit Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"upfrontMargin"}
                            disabled={!row.isUpfront}
                            onChange={(e) => editRow(row, "upfrontMargin", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "upfrontPriceDiscountPercent",
            label: "Unit Upfront Sell Discount %",
            header: <>%<br />Unit Sell Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"upfrontPriceDiscountPercent"}
                            disabled={!row.isUpfront}
                            onChange={(e) => editRow(row, "upfrontPriceDiscountPercent", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "upfrontPriceDiscountValue",
            label: "Unit Upfront Sell Discount £",
            header: <>£<br />Unit Sell Discount</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"upfrontPriceDiscountValue"}
                            disabled={!row.isUpfront}
                            onChange={(e) => editRow(row, "upfrontPriceDiscountValue", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "totalOriginalUpfrontCost",
            label: "Total Upfront List Cost",
            header: <>Total List Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn 
                        row={row}
                        columnKey={"totalOriginalUpfrontCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }            
        },        
        {
            key: "totalUpfrontCost",
            label: "Total Upfront Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",          
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontCost"}
                            disabled={!row.items.find(childItem => childItem.isUpfront && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontCost"}
                            disabled={!row.isUpfront || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalOriginalUpfrontPrice",
            label: "Total Upfront List Sell",
            header: <>Total List Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn 
                        row={row}
                        columnKey={"totalOriginalUpfrontPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }            
        },
        {
            key: "totalUpfrontPrice",
            label: "Total Upfront Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontPrice"}
                            disabled={!row.items.find(childItem => childItem.isUpfront && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontPrice"}
                            disabled={!row.isUpfront || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalUpfrontMarginValue",
            label: "Total Upfront Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "totalUpfrontMarginValue", e.target.value)}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontMargin",
            label: "Total Upfront Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalUpfrontPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalUpfrontMarginValue / footer.totalUpfrontPrice) * 100) : 0
            },            
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontMargin"}
                            disabled={!row.items.find(childItem => childItem.isUpfront && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, "totalUpfrontMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontMargin"}
                            disabled={!row.isUpfront || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalUpfrontMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalUpfrontPriceDiscountPercent",
            label: "Total Upfront Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                if (footer.totalUpfrontPriceDiscountValue <= 0 || footer.totalOriginalUpfrontPrice === 0) return 0;

                return ((footer.totalUpfrontPriceDiscountValue / footer.totalOriginalUpfrontPrice) * 100).toFixed(2)        
            },             
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontPriceDiscountPercent"}
                            disabled={!row.items.find(childItem => childItem.isUpfront && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, "totalUpfrontPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontPriceDiscountPercent"}
                            disabled={!row.isUpfront || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalUpfrontPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalUpfrontPriceDiscountValue",
            label: "Total Upfront Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontPriceDiscountValue"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalUpfrontPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalUpfrontPriceDiscountValue"}
                            disabled={!row.isUpfront || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalUpfrontPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        }        
    ].map(x => ({...x, group: "Upfront",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    }));

    const billingScheduleColumns = [
        {
            key: "recurringStartMonth",
            label: "Start Month",
            header: <>Start Month</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,            
            dataType: "number",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringStartMonth"}
                        disabled={!row.isRecurring || lockState?.isLocked}
                        onChange={(e) => editRow(row, "recurringStartMonth", e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringIntervalMonths",
            label: "Interval Months",
            header: <>Interval Months</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringIntervalMonths"}
                        disabled={!row.isRecurring || lockState?.isLocked}
                        onChange={(e) => editRow(row, "recurringIntervalMonths", e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringNumPayments",
            label: "Num Payments",
            header: <>Num Payments</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringNumPayments"}
                        disabled={!row.isRecurring || lockState?.isLocked}
                        onChange={(e) => editRow(row, "recurringNumPayments", e.target.value)}
                    />
                )
            }
        }, 
        {
            key: "startDelayDescription",
            label: "Start Delay Description",
            header: <>Start Delay</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "80px", maxWidth: "80px" },
            showInFooter: false,
            dataType: "text"
        },
        {
            key: "intervalDescription",
            label: "Interval Description",
            header: <>Interval Description</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "80px", maxWidth: "80px" },
            showInFooter: false,
            dataType: "text"
        },    
        {
            key: "billingDescription",
            label: "Billing Description",
            header: <>Billing Description</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "80px", maxWidth: "80px" },
            showInFooter: false,
            dataType: "text"
        },        
        {
            key: "termLengthDescription",
            label: "Term Length Description",
            header: <>Term Length</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            showInFooter: false,
            dataType: "text"
        },
        {
            key: "upfrontRecognitionMonths",
            label: "Upfront Recognition Months",
            header: <>Upfront Recognition Months</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                const fieldName = "upfrontRecognitionMonths"
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        }
    ].map(x => ({...x, group: "Billing Schedule",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    }));

    const recurringColumns = [
        {
            key: "originalRecurringCost",
            label: "Unit Recurring List Cost",
            header: <>Unit List Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"originalRecurringCost"}
                        disabled={true}
                        onChange={(e) => editRow(row, "originalRecurringCost", e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringCost",
            label: "Unit Recurring Cost",
            header: <>Unit Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringCost"}
                        disabled={!row.isRecurring}
                        onChange={(e) => editRow(row, "recurringCost", e.target.value)}
                    />
                )
            }
        },
        {
            key: "originalRecurringPrice",
            label: "Unit Recurring List Sell",
            header: <>Unit List Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"originalRecurringPrice"}
                        disabled={true}
                        onChange={(e) => editRow(row, "originalRecurringPrice", e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringPrice",
            label: "Unit Recurring Sell",
            header: <>Unit Sell</>,
            className: "p-1 fixed-col-100",
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringPrice"}
                        disabled={!row.isRecurring}
                        onChange={(e) => editRow(row, "recurringPrice", e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringMarginValue",
            label: "Unit Recurring Margin £",
            header: <>£<br />Unit Margin</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "recurringMarginValue", e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringMargin",
            label: "Unit Recurring Margin %",
            header: <>%<br />Unit Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"recurringMargin"}
                            disabled={!row.isRecurring}
                            onChange={(e) => editRow(row, "recurringMargin", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "recurringPriceDiscountPercent",
            label: "Unit Recurring Sell Discount %",
            header: <>%<br />Unit Sell Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"recurringPriceDiscountPercent"}
                            disabled={!row.isRecurring}
                            onChange={(e) => editRow(row, "recurringPriceDiscountPercent", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "recurringPriceDiscountValue",
            label: "Unit Recurring Sell Discount £",
            header: <>£<br />Unit Sell Discount</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"recurringPriceDiscountValue"}
                            disabled={!row.isRecurring}
                            onChange={(e) => editRow(row, "recurringPriceDiscountValue", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "totalOriginalRecurringCost",
            label: "Total Recurring List Cost",
            header: <>Total List Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn 
                        row={row}
                        columnKey={"totalOriginalRecurringCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                ) 
            }        
        },         
        {
            key: "totalRecurringCost",
            label: "Total Recurring Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringCost"}
                            disabled={!row.items.find(childItem => childItem.isRecurring && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringCost"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalOriginalRecurringPrice",
            label: "Total Recurring List Sell",
            header: <>Total List Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn 
                        row={row}
                        columnKey={"totalOriginalRecurringPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }            
        },          
        {
            key: "totalRecurringPrice",
            label: "Total Recurring Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringPrice"}
                            disabled={!row.items.find(childItem => childItem.isRecurring && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringPrice"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, e.target.name, e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalRecurringMarginValue",
            label: "Total Recurring Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "totalRecurringMarginValue", e.target.value)}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringMargin",
            label: "Total Recurring Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalRecurringPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalRecurringMarginValue / footer.totalRecurringPrice) * 100) : 0
            }, 
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringMargin"}
                            disabled={!row.items.find(childItem => childItem.isRecurring && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, "totalRecurringMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringMargin"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalRecurringMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalRecurringPriceDiscountPercent",
            label: "Total Recurring Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalOriginalRecurringPrice
                    ? ((RoundToTwoDecimalPlaces(footer.totalOriginalRecurringPrice - footer.totalRecurringPrice) / footer.totalOriginalRecurringPrice) * 100).toFixed(2)
                    : 0
            },             
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringPriceDiscountPercent"}
                            disabled={!row.items.find(childItem => childItem.isRecurring && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, "totalRecurringPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringPriceDiscountPercent"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalRecurringPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalRecurringPriceDiscountValue",
            label: "Total Recurring Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringPriceDiscountValue"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalRecurringPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalRecurringPriceDiscountValue"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalRecurringPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        }
    ].map(x => ({
        ...x, group: "Recurring",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    }));

    const annualColumns = [
        {
            key: "annualCost",
            label: "Unit Annual Cost",
            header: <>Unit Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"annualCost"}
                        disabled={!row.isRecurring}
                        hidden={isExpanded}
                        onChange={(e) => editRow(row, "annualCost", e.target.value)}
                    />
                )
            }
        },
        {
            key: "annualPrice",
            label: "Unit Annual Sell",
            header: <>Unit Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"annualPrice"}
                        disabled={!row.isRecurring}
                        hidden={isExpanded}
                        onChange={(e) => editRow(row, "annualPrice", e.target.value)}
                    />
                )
            }
        },
        {
            key: "annualMargin",
            label: "Unit Annual Margin %",
            header: <>%<br />Unit Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"annualMargin"}
                            disabled={!row.isRecurring}
                            onChange={(e) => editRow(row, "annualMargin", e.target.value)}
                        />
                    )
                }
            }
        },
        {
            key: "annualMarginValue",
            label: "Unit Annual Margin £",
            header: <>£<br />Unit Margin</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"annualMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "annualMarginValue", e.target.value)}
                    />
                )
            }
        },        
        {
            key: "totalAnnualCost",
            label: "Total Annual Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualPrice",
            label: "Total Annual Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualMargin",
            label: "Total Annual Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                // I used the recurring values, rather than annual.  This is because annual is derived from recurring.
                // If I use annual, I may get incorrect results in cases where there's a mix of term lengths.
                return footer.totalRecurringPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalRecurringMarginValue / footer.totalRecurringPrice) * 100) : 0
            },            
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalAnnualMargin"}
                            disabled={!row.items.find(childItem => childItem.isRecurring && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, "totalAnnualMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalAnnualMargin"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalAnnualMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalAnnualMarginValue",
            label: "Total Annual Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "totalAnnualMarginValue", e.target.value)}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualPriceDiscountPercent",
            label: "Total Annual Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                // I used the recurring values, rather than annual.  This is because annual is derived from recurring.
                // If I use annual, I may get incorrect results in cases where there's a mix of term lengths.
                if (footer.totalRecurringPrice <= 0 || footer.totalOriginalRecurringPrice === 0) return 0;    

                return ((RoundToTwoDecimalPlaces(footer.totalOriginalRecurringPrice - footer.totalRecurringPrice) / footer.totalOriginalRecurringPrice) * 100).toFixed(2)
            },            
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalAnnualPriceDiscountPercent"}
                            disabled={!row.items.find(childItem => childItem.isRecurring && Number(childItem.quantity) !== 0)}
                            onChange={(e) => editRow(row, "totalAnnualPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalAnnualPriceDiscountPercent"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalAnnualPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalAnnualPriceDiscountValue",
            label: "Total Annual Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalAnnualPriceDiscountValue"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalAnnualPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalAnnualPriceDiscountValue"}
                            disabled={!row.isRecurring || Number(row.quantity) === 0}
                            onChange={(e) => editRow(row, "totalAnnualPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        }
    ].map(x => ({
        ...x, group: "Annual",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    }));

    const contractColumns = [
        {
            key: "totalContractCost",
            label: "Total Contract Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractPrice",
            label: "Total Contract Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractMargin",
            label: "Total Contract Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalContractPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalContractMarginValue / footer.totalContractPrice) * 100) : 0
            },            
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalContractMargin"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalContractMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalContractMargin"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalContractMargin", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalContractMarginValue",
            label: "Total Contract Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractMarginValue"}
                        disabled={true}
                        onChange={(e) => editRow(row, "totalContractMarginValue", e.target.value)}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractPriceDiscountPercent",
            label: "Total Contract Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                const totalOriginalContractPrice = footer.totalOriginalUpfrontPrice + footer.totalOriginalRecurringPrice;
                const totalContractPrice = footer.totalUpfrontPrice + footer.totalRecurringPrice;

                return totalOriginalContractPrice
                    ? ((RoundToTwoDecimalPlaces(totalOriginalContractPrice - totalContractPrice) / totalOriginalContractPrice) * 100).toFixed(2)
                    : 0
            },             
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalContractPriceDiscountPercent"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalContractPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalContractPriceDiscountPercent"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalContractPriceDiscountPercent", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        },
        {
            key: "totalContractPriceDiscountValue",
            label: "Total Contract Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalContractPriceDiscountValue"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalContractPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                } else {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"totalContractPriceDiscountValue"}
                            disabled={true}
                            onChange={(e) => editRow(row, "totalContractPriceDiscountValue", e.target.value)}
                            hidden={isExpanded}
                        />
                    )
                }
            }
        }        
    ].map(x => ({
        ...x, group: "Contract",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    }));

    const actionsColumns = [
        {
            key: "disabled",
            label: "Included?",
            group: "Actions",
            isSystem: false,
            isDefault: true,
            isFixed: true,
            pinLocked: true,
            style: { width: "70px" },
            fixDirection: "right",
            align: "right",
            className: "p-1",
            render: (_, row) => {
                return <NestedTableSwitchColumn
                    row={row}
                    propertyName="disabled"
                    invertValue={true}
                    quoteItems={quoteItems}
                    updateQuoteItem={updateQuoteItem}
                    updateQuoteItems={updateQuoteItems}
                 />
            }
        }, {
            key: "actions",
            group: "Actions", // Means that you won't see it on the column settings dialog
            label: "",
            settingsLabel: "Action Buttons",
            isSystem: true,
            isFixed: true,
            pinLocked: true,
            fixDirection: "right",
            style: { width: "95px" },
            align: "right",
            render: (value, row) => {
                if (row.items) return null; // Don't show action buttons for parent rows

                const latestCommercialsAreKnown = !!row.latestCommercials;
                const canUpdateToCatalogue = !!(row.itemLookupId && row.commercialsAreOutdated && latestCommercialsAreKnown);
                const changeSummary = latestCommercialsAreKnown ? getCatalogueCommercialChangeSummary(row) : [];
                const restoreChangeSummary = getRestoreCommercialChangeSummary(row);
                const canRestoreCommercials = restoreChangeSummary.length > 0;

                return (
                    <div className={`d-flex justify-content-end align-items-center gap-2 w-100  ${lockState?.isLocked ? "locked" : ""}`}>
                        <>
                            <div style={{ visibility: canRestoreCommercials ? "visible" : "hidden" }}>
                                <ActionButton
                                    id={`restore-prices-btn-${sanitizeForId(row.guid)}`}
                                    color="secondary"
                                    outline
                                    title="Reset costs/prices"
                                    icon="bi-arrow-counterclockwise"
                                    confirmTitle="Reset costs/prices?"
                                    confirmDialogSize="md"
                                    confirmMessage={
                                        <div className="text-start">
                                            <strong>{row.upfrontNameOnQuote ?? row.recurringNameOnQuote}</strong>
                                            {row.internalName !== (row.upfrontNameOnQuote ?? row.recurringNameOnQuote) ? <div className="text-muted">({row.internalName})</div> : ""}
                                            <div className="my-3">The following fields will change:</div>
                                            <div className="table-responsive mb-3">
                                                <table className="table table-sm table-bordered align-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Field</th>
                                                            <th>Old Value</th>
                                                            <th>New Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {restoreChangeSummary.map((change) => (
                                                            <tr key={`${row.guid}-${change.key}`}>
                                                                <td><strong>{change.label}</strong></td>
                                                                <td>{change.from}</td>
                                                                <td>{change.to}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    }
                                    confirmButtonLabel="Reset"
                                    confirmButtonColor="secondary"
                                    onClick={() => updateQuoteItem(buildRestoreCommercialUpdate(row))}
                                />
                            </div>
                            <div style={{ visibility: canUpdateToCatalogue && changeSummary.length > 0 ? "visible" : "hidden" }}>
                                <ActionButton
                                    id={`update-to-catalogue-btn-${sanitizeForId(row.guid)}`}
                                    color="secondary"
                                    outline
                                    title="Update to latest commercials"
                                    icon="bi-cloud-arrow-down"
                                    disabled={!latestCommercialsAreKnown}
                                    confirmTitle="Apply Latest Catalogue Commercials?"
                                    confirmDialogSize="xl"
                                    confirmMessage={
                                        <div className="text-start">
                                            {!latestCommercialsAreKnown && (
                                                <div className="mb-1">Latest catalogue commercials are not available for this row yet.</div>
                                            )}
                                            {latestCommercialsAreKnown && (
                                                <>
                                                    <strong>{row.upfrontNameOnQuote ?? row.recurringNameOnQuote}</strong>
                                                    {row.internalName !== (row.upfrontNameOnQuote ?? row.recurringNameOnQuote) ? <div className="text-muted">({row.internalName})</div> : ""}
                                                    <div className="my-3">The following fields will change:</div>
                                                    <div className="table-responsive mb-3">
                                                        <table className="table table-sm table-bordered align-middle mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>Field</th>
                                                                    <th>Old Value</th>
                                                                    <th>New Value</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {changeSummary.slice(0, 10).map((change) => (
                                                                    <tr key={`${row.guid}-${change.key}`}>
                                                                        <td><strong>{change.label}</strong></td>
                                                                        <td>{change.from}</td>
                                                                        <td>{change.to}</td>
                                                                    </tr>
                                                                ))}
                                                                {changeSummary.length > 10 && (
                                                                    <tr>
                                                                        <td colSpan={3} className="text-muted">
                                                                            ...and {changeSummary.length - 10} more changes.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <hr></hr>
                                                    <div className="small text-muted">
                                                        <i className="bi bi-info-circle-fill me-2"></i>Cost and sell values you have already overridden are preserved unless they still match the original list values.
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    }
                                    confirmButtonLabel="Apply Update"
                                    confirmButtonColor="secondary"
                                    onClick={() => updateQuoteItem(buildCatalogueCommercialUpdate(row))}
                                />
                            </div>
                        </>
                    </div>
                );
            }
        }
    ]

    const columns = [
        ...mainColumns,
        ...billingScheduleColumns,
        ...upfrontColumns,
        ...recurringColumns,
        ...annualColumns,
        ...contractColumns,
        ...inUseItemLookupFields,
        ...actionsColumns
    ].map(x => (
        { ...x, align: x.align || "right" }
    ));

    const columnProfiles = {
        All: [
            { key: "source", isDefault: false, isRequired: false },
            { key: "itemNumber", isDefault: false, isRequired: false },
            { key: "quantity", isDefault: false },
            { key: "originalUpfrontCost", isDefault: false },
            { key: "upfrontCost", isDefault: false },
            { key: "originalUpfrontPrice", isDefault: false },
            { key: "upfrontPrice", isDefault: false },
            { key: "upfrontMargin", isDefault: false },
            { key: "upfrontMarginValue", isDefault: false },
            { key: "upfrontPriceDiscountPercent", isDefault: false },
            { key: "upfrontPriceDiscountValue", isDefault: false },
            { key: "separator", isDefault: true },
            { key: "originalRecurringCost", isDefault: false },
            { key: "recurringCost", isDefault: false },
            { key: "originalRecurringPrice", isDefault: false },
            { key: "startDelayDescription", isDefault: false },
            { key: "intervalDescription", isDefault: false },
            { key: "billingDescription", isDefault: true },
            { key: "termLengthDescription", isDefault: false },
            { key: "recurringPrice", isDefault: false },
            { key: "recurringMarginValue", isDefault: false },
            { key: "recurringStartMonth", isDefault: false },
            { key: "recurringIntervalMonths", isDefault: false },
            { key: "upfrontRecognitionMonths", isDefault: false },
            { key: "recurringNumPayments", isDefault: false },
            { key: "recurringMargin", isDefault: false },
            { key: "recurringPriceDiscountPercent", isDefault: false },
            { key: "recurringPriceDiscountValue", isDefault: false },
            { key: "annualCost", isDefault: false },
            { key: "annualPrice", isDefault: false },
            { key: "annualMargin", isDefault: false },
            { key: "annualMarginValue", isDefault: false },
            { key: "description", isDefault: true, isRequired: true },
            { key: "nameOnQuote", isDefault: false },
            { key: "autotaskCode", isDefault: false },
            { key: "salesforceCode", isDefault: false },
            { key: "supplierId", isDefault: false },

            { key: "itemLookupTypeId", isDefault: false },
            { key: "quantity", isDefault: true },
            { key: "totalOriginalUpfrontCost", isDefault: false },
            { key: "totalOriginalUpfrontPrice", isDefault: false },
            { key: "totalUpfrontCost", isDefault: true },
            { key: "totalUpfrontPrice", isDefault: true },
            { key: "totalUpfrontPriceDiscountValue", isDefault: false },
            { key: "totalUpfrontPriceDiscountPercent", isDefault: false },
            { key: "totalUpfrontMargin", isDefault: false },
            { key: "totalUpfrontMarginValue", isDefault: true },
            { key: "separator", isDefault: true },
            { key: "totalOriginalRecurringCost", isDefault: false },
            { key: "totalOriginalRecurringPrice", isDefault: false },
            { key: "totalRecurringCost", isDefault: false },
            { key: "totalRecurringPrice", isDefault: false },
            { key: "totalRecurringPriceDiscountValue", isDefault: false },
            { key: "totalRecurringPriceDiscountPercent", isDefault: false },
            { key: "totalRecurringMargin", isDefault: false },
            { key: "totalRecurringMarginValue", isDefault: false },
            { key: "totalAnnualCost", isDefault: true },
            { key: "totalAnnualPrice", isDefault: true },
            { key: "totalAnnualPriceDiscountValue", isDefault: false },
            { key: "totalAnnualPriceDiscountPercent", isDefault: false },
            { key: "totalAnnualMargin", isDefault: false },
            { key: "totalAnnualMarginValue", isDefault: true },
            { key: "totalContractCost", isDefault: false },
            { key: "totalContractPrice", isDefault: false },
            { key: "totalContractPriceDiscountValue", isDefault: false },
            { key: "totalContractPriceDiscountPercent", isDefault: false },
            { key: "totalContractMargin", isDefault: false },
            { key: "totalContractMarginValue", isDefault: false },
            ...inUseItemLookupFields.map(col => ({ key: col.key, isDefault: false })),
            { key: "disabled", isDefault: false, isRequired: false, isFixed: true },
            { key: "actions", isDefault: true, isRequired: true }
        ],
        Upfront: [
            { key: "source", isDefault: false, isRequired: false },
            { key: "itemNumber", isDefault: false, isRequired: false },
            { key: "description", isDefault: true, isRequired: true },
            { key: "nameOnQuote", isDefault: false },
            { key: "autotaskCode", isDefault: false },
            { key: "salesforceCode", isDefault: false },
            { key: "itemLookupTypeId", isDefault: false },
            { key: "quantity", isDefault: false },           
            { key: "billingDescription", isDefault: false },
            { key: "upfrontRecognitionMonths", isDefault: false },
            { key: "originalUpfrontCost", isDefault: false },
            { key: "upfrontCost", isDefault: true },
            { key: "originalUpfrontPrice", isDefault: false },
            { key: "upfrontPrice", isDefault: true },
            { key: "upfrontMargin", isDefault: false },
            { key: "upfrontMarginValue", isDefault: false },

            { key: "upfrontPriceDiscountPercent", isDefault: false },
            { key: "upfrontPriceDiscountValue", isDefault: false },

            { key: "totalUpfrontCost", isDefault: true },
            { key: "totalUpfrontPrice", isDefault: true },
            { key: "totalUpfrontMargin", isDefault: true },
            { key: "totalUpfrontMarginValue", isDefault: true },
            { key: "totalUpfrontPriceDiscountPercent", isDefault: false },
            { key: "totalUpfrontPriceDiscountValue", isDefault: false },
            { key: "supplierId", isDefault: false },
            { key: "totalOriginalUpfrontCost", isDefault: false },
            { key: "totalOriginalUpfrontPrice", isDefault: false },
            ...inUseItemLookupFields.map(col => ({ key: col.key, isDefault: false })),
            { key: "disabled", isDefault: false, isRequired: false, isFixed: true },
            { key: "actions", isDefault: true, isRequired: true }
        ],
        Recurring: [
            { key: "source", isDefault: false, isRequired: false },
            { key: "itemNumber", isDefault: false, isRequired: false },
            { key: "description", isDefault: true, isRequired: true },
            { key: "nameOnQuote", isDefault: false },
            { key: "autotaskCode", isDefault: false },
            { key: "salesforceCode", isDefault: false },
            { key: "itemLookupTypeId", isDefault: false },
            { key: "quantity", isDefault: false },
            { key: "originalRecurringCost", isDefault: false },
            { key: "recurringCost", isDefault: true },
            { key: "originalRecurringPrice", isDefault: false },
            { key: "recurringPrice", isDefault: true },
            { key: "recurringStartMonth", isDefault: true },
            { key: "recurringIntervalMonths", isDefault: true },
            { key: "recurringNumPayments", isDefault: true },
            { key: "startDelayDescription", isDefault: false },
            { key: "intervalDescription", isDefault: false },
            { key: "billingDescription", isDefault: true },
            { key: "termLengthDescription", isDefault: false },

            { key: "recurringMargin", isDefault: false },
            { key: "recurringMarginValue", isDefault: false },
            { key: "recurringPriceDiscountPercent", isDefault: false },
            { key: "recurringPriceDiscountValue", isDefault: false },

            { key: "annualCost", isDefault: false },
            { key: "annualPrice", isDefault: false },
            { key: "annualMargin", isDefault: false },
            { key: "annualMarginValue", isDefault: false },
            { key: "totalAnnualCost", isDefault: false },
            { key: "totalAnnualPrice", isDefault: false },
            { key: "totalAnnualPriceDiscountValue", isDefault: false },
            { key: "totalAnnualPriceDiscountPercent", isDefault: false },
            { key: "totalAnnualMargin", isDefault: false },
            { key: "totalAnnualMarginValue", isDefault: false },
            { key: "totalRecurringCost", isDefault: true },
            { key: "totalRecurringPrice", isDefault: true },
            { key: "totalRecurringMargin", isDefault: true },
            { key: "totalRecurringMarginValue", isDefault: true },
            { key: "totalRecurringPriceDiscountPercent", isDefault: false },
            { key: "totalRecurringPriceDiscountValue", isDefault: false },
            { key: "supplierId", isDefault: false },
            { key: "totalOriginalRecurringCost", isDefault: false },
            { key: "totalOriginalRecurringPrice", isDefault: false },
            ...inUseItemLookupFields.map(col => ({ key: col.key, isDefault: false })),
            { key: "disabled", isDefault: false, isRequired: false, isFixed: true },
            { key: "actions", isDefault: true, isRequired: true }
        ],
        Custom: [
            ...columns.map(col => ({
            key: col.key,
            isDefault: (col.key === "description" || col.key === "actions"),
            isRequired: (col.key === "description" || col.key === "actions")
            })),
            ...inUseItemLookupFields.map(col => ({ key: col.key, isDefault: false }))
        ]
    };


    //totals
    const getTotals = (items) => {
        let totals = {
            totalUpfrontCost: 0,
            totalUpfrontPrice: 0,
            totalRecurringCost: 0,
            totalRecurringPrice: 0,
            totalOriginalUpfrontPrice: 0,
            totalOriginalRecurringPrice: 0,
            totalUpfrontPriceDiscountPercent: 0,
            totalUpfrontPriceDiscountValue: 0,
            totalRecurringPriceDiscountPercent: 0,
            totalRecurringPriceDiscountValue: 0,
            totalAnnualPriceDiscountPercent: 0,
            totalAnnualPriceDiscountValue: 0,
            totalContractPriceDiscountPercent: 0,
            totalContractPriceDiscountValue: 0,
            totalContractCost: 0,
            totalContractPrice: 0,
            totalAnnualCost: 0,
            totalAnnualPrice: 0
        };

        for (const item of items) {
            const multiplier = item.isCreditItem ? -item.quantity : item.quantity;

            totals.totalUpfrontCost += parseFloat(item.totalUpfrontCost || 0);
            totals.totalUpfrontPrice += parseFloat(item.totalUpfrontPrice || 0);
            totals.totalRecurringCost += parseFloat(item.totalRecurringCost || 0);
            totals.totalRecurringPrice += parseFloat(item.totalRecurringPrice || 0);
            totals.totalOriginalUpfrontPrice += parseFloat(item.totalOriginalUpfrontPrice || 0);
            totals.totalOriginalRecurringPrice += parseFloat(item.totalOriginalRecurringPrice || 0);
            totals.totalContractCost += parseFloat(item.totalContractCost || 0);
            totals.totalContractPrice += parseFloat(item.totalContractPrice || 0);
            totals.totalAnnualCost += parseFloat(item.totalAnnualCost || 0);
            totals.totalAnnualPrice += parseFloat(item.totalAnnualPrice || 0);

            if (item.isUpfront && item.originalUpfrontPrice > 0 && item.upfrontPrice < item.originalUpfrontPrice) {
                totals.totalUpfrontPriceDiscountValue += parseFloat(item.originalUpfrontPrice - item.upfrontPrice) * multiplier;
            }

            if (item.isRecurring && item.originalRecurringPrice > 0 && item.recurringPrice < item.originalRecurringPrice) {
                totals.totalRecurringPriceDiscountValue += parseFloat(item.originalRecurringPrice - item.recurringPrice) * multiplier * item.recurringNumPayments;
                totals.totalAnnualPriceDiscountValue += parseFloat(item.originalRecurringPrice - item.recurringPrice) * multiplier * (12 / item.recurringIntervalMonths);
            }

            totals.totalContractPriceDiscountValue = totals.totalUpfrontPriceDiscountValue + totals.totalRecurringPriceDiscountValue;

            // Do NOT include child items in totals
            // if (item.items && Array.isArray(item.items)) {
            //     const childTotals = getTotals(item.items);
            //     totals.totalUpfrontCost += childTotals.totalUpfrontCost;
            //     totals.totalUpfrontPrice += childTotals.totalUpfrontPrice;
            //     totals.recurringTotalCost += childTotals.recurringTotalCost;
            //     totals.totalRecurringPrice += childTotals.totalRecurringPrice;
            // }
        }

        totals.totalUpfrontPriceDiscountPercent = totals.totalOriginalUpfrontPrice
            ? (((totals.totalOriginalUpfrontPrice - totals.totalUpfrontPrice) / totals.totalOriginalUpfrontPrice) * 100).toFixed(2)
            : 0

        totals.totalRecurringPriceDiscountPercent = totals.totalOriginalRecurringPrice
            ? (((totals.totalOriginalRecurringPrice - totals.totalRecurringPrice) / totals.totalOriginalRecurringPrice) * 100).toFixed(2)
            : 0

        // Interval doesn't change the percent discount, so we can just copy it from the recurring value we already calculated
        totals.totalAnnualPriceDiscountPercent = totals.totalRecurringPriceDiscountPercent

        totals.totalContractPriceDiscountPercent = totals.totalOriginalUpfrontPrice + totals.totalOriginalRecurringPrice
            ? (
                ((totals.totalOriginalUpfrontPrice + totals.totalOriginalRecurringPrice) - (totals.totalUpfrontPrice + totals.totalRecurringPrice))
                / (totals.totalOriginalUpfrontPrice + totals.totalOriginalRecurringPrice)
                * 100
            ).toFixed(2)
            : 0

        return totals;
    };


    const {
        totalUpfrontCost,
        totalUpfrontPrice,
        totalRecurringCost,
        totalRecurringPrice,
        totalUpfrontPriceDiscountPercent,
        totalUpfrontPriceDiscountValue,
        totalRecurringPriceDiscountPercent,
        totalRecurringPriceDiscountValue,
        totalAnnualCost,
        totalAnnualPrice,
        totalAnnualPriceDiscountPercent,
        totalAnnualPriceDiscountValue,
        totalContractCost,
        totalContractPrice,
        totalContractPriceDiscountPercent,
        totalContractPriceDiscountValue
    } = getTotals(quoteItems);

    const totalUpfrontMargin = totalUpfrontPrice
        ? ((totalUpfrontPrice - totalUpfrontCost) / totalUpfrontPrice * 100)
        : 0.00;

    const totalRecurringMargin = totalRecurringPrice
        ? ((totalRecurringPrice - totalRecurringCost) / totalRecurringPrice * 100)
        : 0.00;

    const totalAnnualMargin = totalAnnualPrice
        ? ((totalAnnualPrice - totalAnnualCost) / totalAnnualPrice * 100)
        : 0.00;
    const totalContractMargin = totalContractPrice
        ? ((totalContractPrice - totalContractCost) / totalContractPrice * 100)
        : 0.00;



    //--- Footer ---
    //const visibleColsInOrder = columns.filter(col => visibleColumns[col.key]);

    const changeFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value
    }


/*
    const renderFooterCell = (col) => {
        switch (col.key) {
            case "description":
                return <td key={col.key} style={{ fontWeight: "bold" }}>Total</td>;

            case "itemLookupTypeId":
            case "quantity":
            case "upfrontCost":
            case "upfrontPrice":
            case "upfrontMargin":
            case "recurringCost":
            case "recurringPrice":
            case "annualCost":
            case "annualPrice":
            case "recurringMargin":
            case undefined: // any keyless columns (e.g., action icons) if they sneak through
                return <td key={col.key || Math.random()}></td>;

            case "separator":
                return (
                    <td key={col.key} className="position-relative px-0" style={{ width: "1px" }}>
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left: "50%",
                                width: "1px",
                                backgroundColor: "var(--text-color)",
                            }}
                        />
                    </td>
                );

            case "totalUpfrontCost":
                return formattedFooterCell(col, totalUpfrontCost, 'currency');

            case "totalUpfrontPrice":
                return formattedFooterCell(col, totalUpfrontPrice, 'currency');
            case "totalUpfrontMargin":
                return formattedFooterCell(col, totalUpfrontMargin, 'percentage');
            case "totalUpfrontMarginValue":
                return formattedFooterCell(col, totalUpfrontPrice - totalUpfrontCost, "currency");
            case "totalUpfrontPriceDiscountPercent":
                return formattedFooterCell(col, totalUpfrontPriceDiscountPercent, 'percentage');
            case "totalUpfrontPriceDiscountValue":
                return formattedFooterCell(col, totalUpfrontPriceDiscountValue, "currency");
            case "totalRecurringCost": // <-- correct key
                return formattedFooterCell(col, totalRecurringCost, 'currency');
            case "totalRecurringPrice":
                return formattedFooterCell(col, totalRecurringPrice, 'currency');
            case "totalRecurringMargin":
                return formattedFooterCell(col, totalRecurringMargin, 'percentage');
            case "totalRecurringMarginValue":
                return formattedFooterCell(col, totalRecurringPrice - totalRecurringCost, "currency");
            case "totalRecurringPriceDiscountPercent":
                return formattedFooterCell(col, totalRecurringPriceDiscountPercent, 'percentage');
            case "totalRecurringPriceDiscountValue":
                return formattedFooterCell(col, totalRecurringPriceDiscountValue, "currency");
            case "totalAnnualCost":
                return formattedFooterCell(col, totalAnnualCost, 'currency');

            case "totalAnnualPrice":
                return formattedFooterCell(col, totalAnnualPrice, 'currency');

            case "totalAnnualMargin":
                return formattedFooterCell(col, totalAnnualMargin, 'percentage');
            case "totalAnnualMarginValue":
                return formattedFooterCell(col, totalAnnualPrice - totalAnnualCost, "currency");
            case "totalAnnualPriceDiscountPercent":
                return formattedFooterCell(col, totalAnnualPriceDiscountPercent, 'percentage');
            case "totalAnnualPriceDiscountValue":
                return formattedFooterCell(col, totalAnnualPriceDiscountValue, "currency");
            case "totalContractPriceDiscountPercent":
                return formattedFooterCell(col, totalContractPriceDiscountPercent, 'percentage');
            case "totalContractPriceDiscountValue":
                return formattedFooterCell(col, totalContractPriceDiscountValue, "currency");
            case "totalContractCost":
                return formattedFooterCell(col, totalContractCost, 'currency');
            case "totalContractPrice":
                return formattedFooterCell(col, totalContractPrice, 'currency');
            case "totalContractMargin":
                return formattedFooterCell(col, totalContractMargin, 'percentage');
            case "totalContractMarginValue":
                return formattedFooterCell(col, totalContractPrice - totalContractCost, "currency");
            default:
                return <td key={col.key}></td>;
        }
    };
*/
    return (
        <>
            <CardHeader>
                <Row>
                    <Col style={{ display: "inline-flex" }}>
                        {Object.keys(columnProfiles).map((tab) => (
                            <h6
                                key={tab}
                                className={
                                    currentActiveMainTab === tab
                                        ? "custom-nav-selected custom-nav m-3"
                                        : "custom-nav m-3"
                                }
                                style={{ cursor: "pointer" }}
                                onClick={() => setCurrentActiveMainTab(tab)}
                            >
                                {tab}
                            </h6>
                        ))}
                    </Col>
                </Row>
            </CardHeader>
            <CardBody style={{ overflow: "hidden", maxWidth: "100%", width: "100%" }}>
                <DataTableWindowed
                    id="commercial-quote-items-table"
                    height="600px"
                    columns={columns}
                    data={data}
                    isUpdating={isUpdating}
                    error={false}
                    paging={false}
                    shrinkHeightToContents={true}
                    //searchableFields={["name"]}     
                    onSelectedDataChange={(arr) => {
                        // We intentionally discard the row objects from `arr` and store only the IDs.
                        // The rows here come from DataTableWindowed's internal display-transformed data
                        // (customFilteredData), which can be stale at the point this callback fires —
                        // for example, immediately after a bulk save, the `data` prop change triggers
                        // this effect before customFilteredData has recomputed with the new values.
                        // By storing only IDs and deriving `selectedData` via useMemo from the live
                        // `quoteItems` prop, we guarantee the bulk modal always receives up-to-date rows.
                        setSelectedIds(new Set(arr.map(x => x.databaseId)));
                    }}              
                    actions={[
                        <Button
                            key="update"
                            className={`${lockState?.isLocked ? "locked" : ""}`}
                            color={theme === "dark-mode" ? "dark" : "light"}
                            outline
                            disabled={selectedItems.length === 0}
                            onClick={() => {
                                setBulkModalOpen(true);
                            }}
                        >
                            Update Selected
                        </Button>                       
                    ]}
                    showAutogeneratedFooter={true}
                    columnProfiles={columnProfiles}
                    allowColumnCustomisation={true}
                searchableFields={[
                    ...mainColumns.map(column => column.key),
                    ...inUseItemLookupFields.map(field => (
                    field.key
                )), ]}
                XXX_filters={
                    /* RENAMED THIS TO XXX_filters for now, so it doesn't show, as not sure if we really want this feature */

                    inUseItemLookupFields.map(field => ({
                    id: field.key,
                    label: field.label,
                    type: "text",
                    options: [{
                            value: 0,
                            label: 1
                    }],
                    value: "",
                    advanced: true
                }))}
                onFilterChange={changeFilter}
                currentProfileKey={currentActiveMainTab}
                allowMultiSelect={true}
                />
            </CardBody>

            <QuoteItemBulkModal
                modalOpen={bulkModalOpen}
                setModalOpen={setBulkModalOpen}
                items={selectedItems}
                suppliers={suppliers || []}
                itemTypes={itemLookupTypes || []}
                onSave={handleBulkSave}
                showDetailsTab={false}
                showConfigTab={false}
            />            
        </>
    );
}
export default CommercialTable;
